/**
 * Generic, domain-agnostic types for nested infinite-scroll pagination in expandable
 * tables (e.g. Parent → Child → Grandchild, where the inner levels lazy-paginate on
 * scroll). Compose one `useSectionRegistry` per paginating level — do NOT build an
 * N-level config engine. See NESTED-PAGINATION.md.
 */

/** Pagination flags for a single expandable section, surfaced to the scroll observer. */
export interface SectionPaginationSnapshot {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

/** Payload a `SectionLoader` reports up to its registry on every query change. */
export interface SectionLoaderPayload<TRow> {
  rows: TRow[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** True only while the FIRST page is loading (drives the expand-toggle spinner). */
  isLoadingFirstPage: boolean;
  fetchNextPage: () => void;
}

/** Stable API to read a section's pagination state and request its next page, by id. */
export interface SectionPaginationApi {
  getSection: (sectionId: string) => SectionPaginationSnapshot | undefined;
  fetchNextPage: (sectionId: string) => void;
}

/**
 * Callback ref the table attaches to every row so the shared observer can watch section tails.
 * Exported so a screen can compose its own wiring hook and still type what it returns.
 */
export type RegisterRowRef = (rowId: string, el: HTMLTableRowElement | null) => void;

/** A row with a stable, comparable id — used to gate version bumps (rowsSignature). */
export type IdentifiableRow = { id: string | number };

/** Result of `useSectionRegistry` — one paginating level's accumulated state + API. */
export interface SectionRegistryResult<TRow> {
  /** A section's accumulated loaded rows (or undefined if never loaded). Stable getter. */
  getRows: (sectionId: string) => TRow[] | undefined;
  /** Bumps whenever ANY section's row-identity list changes — use as a memo dependency. */
  version: number;
  /** First-page-loading section ids (drives the expand-toggle spinner). */
  loadingSectionIds: Set<string>;
  sectionPagination: SectionPaginationApi;
  /** Stable callback a `SectionLoader` reports its query state through. */
  onSectionChange: (sectionId: string, payload: SectionLoaderPayload<TRow>) => void;
  /**
   * Stable callback a `SectionLoader` calls when it unmounts (section collapsed, filtered
   * out, etc.). Drops the section from all registry state so a loader that unmounts mid-load
   * doesn't leave a stuck first-page-loading flag (→ perpetual expand-toggle spinner).
   */
  onSectionUnmount: (sectionId: string) => void;
}

/** Default staleTime for lazy section queries. */
export const DEFAULT_SECTION_STALE_TIME = 5 * 60 * 1000;

/** Default logging tag for the generic core (callers may override per screen). */
export const DEFAULT_NESTED_PAGINATION_LOG_TAG = 'NestedPagination:';
